import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MantineReactTable } from "mantine-react-table";
import {
  Box,
  TextInput,
  Select,
  Menu,
  ActionIcon,
  Button,
  useMantineColorScheme,
} from "@mantine/core";
import { IconDotsVertical } from "@tabler/icons-react";
import MGAAPI from "../../api";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { useAtom } from "jotai";
import PropTypes from "prop-types";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom";
import { useAssessmentRouteParams, useCurrentDesignVersion } from "../../constants/helper";
import displayError from "../../constants/utils";
import { getErrorMessage } from "../../utils/errorUtils";
import SEConfirmModal from "../common/SEConfirmModal";


const MGA_API = new MGAAPI();
const MAX_SENSITIVITY_FACTORS = 100;
const MAX_ROWS = 20;

// Helper: flatten AssetData/initialDataFromApi to table rows
function flattenComponentsToRows(components) {
  // Defensive: ensure components is always an array
  if (!Array.isArray(components)) return [];
  let rows = [];
  let idCounter = 1;

  // Find the max number of sensitivityValues for any property in all components
  let maxLen = 0;
  components.forEach((comp) => {
    comp.sensitivityValue.forEach((sv) => {
      if (Array.isArray(sv.sensitivityValues)) {
        maxLen = Math.max(maxLen, sv.sensitivityValues.length);
      }
    });
  });

  // Never exceed MAX_SENSITIVITY_FACTORS
  const totalSlots = Math.min(maxLen < MAX_SENSITIVITY_FACTORS ? maxLen + 1 : maxLen, MAX_SENSITIVITY_FACTORS);

  components.forEach((comp) => {
    comp.sensitivityValue.forEach((sv) => {
      // Map sensitivityValues to factors - NO PADDING
      let factors = sv.sensitivityValues.map((val) => ({
        value: val.attributeValue,
        scaling: val.scalingFactor,
      }));
      rows.push({
        id: String(idCounter++), // Ensure unique id for each row
        asset: comp.componentType,
        property: sv.attributeName,
        componentId: comp.componentId,
        attributeRegex: sv.attributeRegex,
        sensitivityFactors: factors,
      });
    });
  });
  return rows;
}

// Helper: build lookup for attribute metadata (regex, desc) by asset/property
function buildAttributeMetaMap(initialSimualtionTable) {
  const map = {};
  (initialSimualtionTable || []).forEach((project) => {
    project.components.forEach((comp) => {
      comp.sensitivityValue.forEach((sv) => {
        map[`${comp.componentType}|||${sv.attributeName}`] = {
          attributeRegex: sv.attributeRegex,
        };
      });
    });
  });
  return map;
}

// Helper: enrich rows with attribute metadata when missing
function enrichRowsWithAttributeMeta(rows, initialSimualtionTable) {
  const metaMap = buildAttributeMetaMap(initialSimualtionTable);
  return (rows || []).map((row) => {
    const key = `${row.asset}|||${row.property}`;
    const meta = metaMap[key] || {};
    return {
      ...row,
      attributeRegex: row.attributeRegex || meta.attributeRegex || "",
    };
  });
}

function areSensitivityFactorsEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index += 1) {
    const left = a[index] || {};
    const right = b[index] || {};
    if (
      (left.value ?? "") !== (right.value ?? "") ||
      (left.scaling ?? "") !== (right.scaling ?? "")
    ) {
      return false;
    }
  }
  return true;
}

function areRowsEqual(currentRows = [], nextRows = []) {
  if (currentRows.length !== nextRows.length) return false;
  for (let index = 0; index < currentRows.length; index += 1) {
    const current = currentRows[index] || {};
    const next = nextRows[index] || {};
    if (
      String(current.id ?? "") !== String(next.id ?? "") ||
      (current.asset ?? "") !== (next.asset ?? "") ||
      (current.property ?? "") !== (next.property ?? "") ||
      (current.componentId ?? "") !== (next.componentId ?? "") ||
      (current.attributeRegex ?? "") !== (next.attributeRegex ?? "")
    ) {
      return false;
    }
    if (
      !areSensitivityFactorsEqual(
        current.sensitivityFactors,
        next.sensitivityFactors
      )
    ) {
      return false;
    }
  }
  return true;
}

// Helper: get all unique assets and their properties from AssetData
function getAssetOptions(assetData) {
  const assetMap = {};
  assetData.forEach((project) => {
    project.components.forEach((comp) => {
      if (!assetMap[comp.componentType])
        assetMap[comp.componentType] = new Set();
      comp.sensitivityValue.forEach((sv) =>
        assetMap[comp.componentType].add(sv.attributeName)
      );
    });
  });
  return Object.entries(assetMap).map(([asset, props]) => ({
    asset,
    properties: Array.from(props),
  }));
}

// Helper: get all property options for an asset
function getPropertyOptions(assetData, asset) {
  const props = new Set();
  assetData.forEach((project) => {
    project.components.forEach((comp) => {
      if (comp.componentType === asset) {
        comp.sensitivityValue.forEach((sv) => props.add(sv.attributeName));
      }
    });
  });
  return Array.from(props);
}

// Helper: get all sensitivity blocks for an asset/property
function getSensitivityBlocks(assetData, asset, property) {
  for (const project of assetData) {
    for (const comp of project.components) {
      if (comp.componentType === asset) {
        const found = comp.sensitivityValue.find(
          (sv) => sv.attributeName === property
        );
        if (found) return found.sensitivityValues;
      }
    }
  }
  return [];
}

// Convert API/AssetData to table row format
function convertApiDataToTableRows(apiData, assetData) {
  // If apiData is empty, use assetData as default
  if (!apiData || apiData.length === 0) {
    return flattenComponentsToRows(assetData[0].components);
  }
  // Otherwise, use apiData, but align with AssetData for missing blocks
  // (Assume apiData is in the same structure as AssetData)
  return flattenComponentsToRows(apiData[0].components);
}

export default function SimulationTable({
  initialSimualtionTable,
  initialSensitivityData,
  callSnackbar,
  apiSimulationData,
  setIsSubmitClicked,
  setSensitivityDirty,
  onSensitivityChange,
  showSimuationResult,
  onCartesianChange
}) {
  const { t } = useTranslation();
  // Memoize assetOptions so it's always available in render
  const assetOptions = useMemo(
    () => getAssetOptions(initialSimualtionTable || []),
    [initialSimualtionTable]
  );
    const [userScope] = useAtom(userScopeAtom);    
  
  // Always start with apiSimulationData if available, else use initialSimualtionTable
  const [data, setData] = useState([]);
  const [hasLocalEdits, setHasLocalEdits] = useState(false);

  // useEffect(() => {
  //   // If the user has unsaved edits, don't overwrite them with incoming data
  //   if (hasLocalEdits) return;
  //   if (
  //     apiSimulationData &&
  //     Array.isArray(apiSimulationData) &&
  //     apiSimulationData.length > 0 &&
  //     apiSimulationData[0] &&
  //     Array.isArray(apiSimulationData[0].components)
  //   ) {
  //     const tableRowData = flattenComponentsToRows(
  //       apiSimulationData[0].components
  //     );
  //     const enrichedRows = enrichRowsWithAttributeMeta(
  //       tableRowData,
  //       initialSimualtionTable
  //     );
  //     setData(enrichedRows || []);
  //     return;
  //   }
  //   // If no apiSimulationData, fall back to initialSimualtionTable once
  //   if (initialSimualtionTable && initialSimualtionTable.length > 0) {
  //     const tableRowData = flattenComponentsToRows(
  //       initialSimualtionTable[0].components || []
  //     );
  //     const enrichedRows = enrichRowsWithAttributeMeta(
  //       tableRowData,
  //       initialSimualtionTable
  //     );
  //     setData(enrichedRows || []);
  //   }
  // }, [initialSimualtionTable, apiSimulationData, hasLocalEdits]);

   useEffect(() => {
    if (hasLocalEdits) return;
    if (
      apiSimulationData &&
      Array.isArray(apiSimulationData) &&
      apiSimulationData.length > 0 &&
      apiSimulationData[0] &&
      Array.isArray(apiSimulationData[0].components)
    ) {
      const tableRowData = flattenComponentsToRows(
        apiSimulationData[0].components
      );
      const enrichedRows = enrichRowsWithAttributeMeta(
        tableRowData,
        initialSimualtionTable
      );
      const nextRows = enrichedRows || [];
      setData((previousRows) =>
        areRowsEqual(previousRows, nextRows) ? previousRows : nextRows
      );
    }
  }, [initialSimualtionTable, apiSimulationData, hasLocalEdits]);


  const [projectDetails] = useAtom(projectDetailsAtom);
  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const [loading, setLoading] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [errorRows, setErrorRows] = useState({});
  const [inputErrors, setInputErrors] = useState({});
  const selectedVersion = useCurrentDesignVersion(projectDetails?.designWithVersionsDTOList)
  const isActionButtonsBlocked =
    selectedVersion?.versionStatus === "COMPLETED" ||
    showSimuationResult?.status === "PROCESSING";

  const hasErrors = useMemo(() => {
    const rowErrorPresent = Object.values(errorRows).some(Boolean);
    const inputErrorPresent = Object.keys(inputErrors).length > 0;
    return rowErrorPresent || inputErrorPresent;
  }, [errorRows, inputErrors]);
  const { colorScheme } = useMantineColorScheme();
  const isLightMode = colorScheme === "light";

  const handleSubmitClick = () => {
    if (hasErrors) {
      displayError("error", t("simulation.error.fixValidation"));
      return;
    }

    const hasScenarioTableData =
      Array.isArray(initialSensitivityData) && initialSensitivityData.length > 0;

    if (hasScenarioTableData) {
      setShowSubmitConfirm(true);
      return;
    }

    convertTableDataToJson(data, setLoading);
  };

  const handleConfirmSubmit = () => {
    setShowSubmitConfirm(false);
    convertTableDataToJson(data, setLoading);
  };
  
  // Ref for table container to control scroll
  const tableContainerRef = React.useRef(null);
  const markDirty = () => {
    if (setSensitivityDirty) setSensitivityDirty(true);
    setHasLocalEdits(true);
    if (onSensitivityChange) onSensitivityChange();
  };

  // Used for validation: get all used asset/property pairs except for a given row id
  const usedAssetPropertyPairs = (excludeId = null) =>
    data
      .filter((r) => r.id !== excludeId)
      .map((r) => `${r.asset}|||${r.property}`);

  // Always recalculate maxBlocks based on current data, but cap at MAX_SENSITIVITY_FACTORS
  let maxBlocks = Math.min(
    Math.max(
      ...data.map((row) => row.sensitivityFactors.length),
      0
    ),
    MAX_SENSITIVITY_FACTORS
  );

  const filledCells = getFilledCellCount(data);

  // Count only filled value/scale pairs (not empty/dummy), EXCLUDING first (reference) value/scale in each row
  function getFilledCellCount(rows) {
    let count = 0;
    for (const row of rows) {
      // Start from index 1 to skip the first (reference) value/scale pair
      for (let i = 1; i < row.sensitivityFactors.length; i++) {
        const sf = row.sensitivityFactors[i];
        if (
          (sf.value !== undefined &&
            sf.value !== null &&
            String(sf.value).trim() !== "") ||
          (sf.scaling !== undefined &&
            sf.scaling !== null &&
            String(sf.scaling).trim() !== "")
        ) {
          count++;
        }
      }
    }
    return count;
  }

  // Count only filled value/scale pairs (not empty/dummy), INCLUDING reference (index 0) in each row
  function getTotalFilledPairs(rows) {
    let count = 0;
    for (const row of rows) {
      for (let i = 0; i < row.sensitivityFactors.length; i++) {
        const sf = row.sensitivityFactors[i];
        if (
          (sf.value !== undefined &&
            sf.value !== null &&
            String(sf.value).trim() !== "") ||
          (sf.scaling !== undefined &&
            sf.scaling !== null &&
            String(sf.scaling).trim() !== "")
        ) {
          count++;
        }
      }
    }
    return count;
  }

  // Count filled pairs in a specific row (excluding first/reference pair)
  function getFilledPairsInRow(row) {
    let count = 0;
    for (let i = 1; i < row.sensitivityFactors.length; i++) {
      const sf = row.sensitivityFactors[i];
      if (
        (sf.value !== undefined &&
          sf.value !== null &&
          String(sf.value).trim() !== "") ||
        (sf.scaling !== undefined &&
          sf.scaling !== null &&
          String(sf.scaling).trim() !== "")
      ) {
        count++;
      }
    }
    return count;
  }

  // Count filled pairs in a row only when BOTH value and scaling are present
  function getCompletedPairCountForRow(row) {
    let count = 0;
    for (let i = 0; i < row.sensitivityFactors.length; i++) {
      const sf = row.sensitivityFactors[i];
      const hasValue =
        sf.value !== undefined &&
        sf.value !== null &&
        String(sf.value).trim() !== "";
      const hasScaling =
        sf.scaling !== undefined &&
        sf.scaling !== null &&
        String(sf.scaling).trim() !== "";
      if (hasValue && hasScaling) {
        count++;
      }
    }
    return count;
  }

  function canAddVariableForRow(targetRow, rows = data) {
    if (!targetRow || targetRow.sensitivityFactors.length >= MAX_SENSITIVITY_FACTORS) {
      return false;
    }

    const currentRowFilledCount = getCompletedPairCountForRow(targetRow);
    let otherRowsProduct = 1;

    for (const otherRow of rows) {
      if (otherRow.id === targetRow.id) continue;
      const filledCount = getCompletedPairCountForRow(otherRow);
      if (filledCount > 0) {
        otherRowsProduct *= filledCount;
      }
    }

    const projectedCurrentRowCount = currentRowFilledCount + 1;
    const projectedProduct = otherRowsProduct * projectedCurrentRowCount;
    return projectedProduct <= MAX_SENSITIVITY_FACTORS;
  }

  // Calculate cartesian product: for each row with data, count filled pairs (including reference)
  // Product = pairs_row1 × pairs_row2 × ... × pairs_rowN
  // This represents all possible combinations across rows
  // Only count pairs where BOTH value AND scaling are filled
  function getCartesianProduct(rows) {
    let product = 1;
    for (const row of rows) {
      const filledCount = getCompletedPairCountForRow(row);
      // Only multiply if row has any filled pairs
      if (filledCount > 0) {
        product *= filledCount;
      }
    }
    return product;
  }

  const cartesianSummary = useMemo(() => {
    const rowCounts = data
      .map((row) => getCompletedPairCountForRow(row))
      .filter((count) => count > 0);

    if (rowCounts.length === 0) {
      return {
        expression: "0",
        total: 0,
        remaining: MAX_SENSITIVITY_FACTORS,
      };
    }

    const total = rowCounts.reduce((acc, count) => acc * count, 1);
    const summary = {
      expression: rowCounts.join(" × "),
      total,
      remaining: Math.max(0, MAX_SENSITIVITY_FACTORS - total),
    };

    return summary;
  }, [data]);

  useEffect(() => {
    if (onCartesianChange) {
      onCartesianChange(cartesianSummary);
    }
  }, [cartesianSummary, onCartesianChange]);

  const addVariableGuidance = useMemo(() => {
    const eligibleRows = data.filter((row) => canAddVariableForRow(row));

    if (eligibleRows.length === 0) {
      return `No rows can add variables now. Reduce combinations or delete a variable to continue (limit: ${MAX_SENSITIVITY_FACTORS}).`;
    }

    const labels = eligibleRows.map(
      (row) => `Row ${row.id}: ${row.asset} / ${row.property} `
    );

    return `Eligible rows for Add Variable:\n${labels.join("\n")}. `;
  }, [data]);

  // Error state for max parameter limit
  const [paramLimitError, setParamLimitError] = useState(false);

  // Validate a value against attributeRegex; empty values are treated as valid
  const validateRegex = (val, pattern) => {
    if (!pattern || val === "" || val === undefined || val === null) {
      return true;
    }
    try {
      // Some APIs send patterns wrapped in slashes; strip leading/trailing slash to avoid double escaping
      const cleaned = pattern.startsWith("/") && pattern.endsWith("/")
        ? pattern.slice(1, -1)
        : pattern;
      const regex = new RegExp(cleaned);
      return regex.test(val);
    } catch (err) {
      console.error("Invalid attributeRegex pattern", pattern, err);
      return true;
    }
  };

  // Function to convert table data to grouped JSON format
  function convertTableDataToJson(tableRows, setLoading) {
    const grouped = {};
    tableRows.forEach((row) => {
      if (!grouped[row.componentId]) {
        grouped[row.componentId] = {
          componentId: row.componentId,
          componentType: row.asset,
          sensitivityValue: [],
        };
      }
      // Find attributeDesc from initialSimualtionTable for this asset/property
      let attributeDesc = "";
      for (const project of initialSimualtionTable || []) {
        for (const comp of project.components) {
          if (comp.componentType === row.asset) {
            const sv = comp.sensitivityValue.find(
              (sv) => sv.attributeName === row.property
            );
            if (sv && sv.attributeDesc) {
              attributeDesc = sv.attributeDesc;
              break;
            }
          }
          if (attributeDesc) break;
        }
      }
      // Filter out sensitivityFactors with empty value
      let filteredFactors = row.sensitivityFactors.filter(
        (sf) => sf.value !== "" && sf.value !== undefined && sf.value !== null
      );
      // Ignore row if it has only one value/scale pair (reference) and it's the only factor
      if (filteredFactors.length <= 1 && row.sensitivityFactors.length === 1) {
        return;
      }
      if (filteredFactors.length > 0) {
        grouped[row.componentId].sensitivityValue.push({
          attributeType: row.attributeType || "COMPONENT",
          attributeName: row.property,
          attributeDesc: attributeDesc,
          sensitivityValues: filteredFactors.map((sf) => ({
            attributeValue: sf.value,
            scalingFactor: sf.scaling,
          })),
        });
      }
    });

    let simulationTable = [];
    // const projectDesignVersion =
    //   projectDetails?.designWithVersionsDTOList?.[0]?.designVersionDTO?.[0];
    simulationTable.push({
      projectId: projectId || "",
      designId: designId || "",
      designVersionId: designVersionId || "",
      components: Object.values(grouped),
    });

    setLoading(true);
    MGA_API.saveProjectSensitivity(simulationTable[0])
      .then((response) => {
        setLoading(false);
        callSnackbar("success", t("simulation.sensitivity.save.success"));
        if (setSensitivityDirty) setSensitivityDirty(false);
        setIsSubmitClicked((prev) => !prev);
        console.log("Sensitivity data saved successfully:", response);
      })
      .catch((error) => {
        setLoading(false);
        callSnackbar("error", getErrorMessage(error));
        setHasLocalEdits(true);
      });
  }

  // Sensitivity columns
  function getSensitivityColumns() {
    const cols = [];
    for (let i = 0; i < maxBlocks; i++) {
      cols.push(
        {
          id: `value_${i}`,
          header: `${t("simulation.table.value")} ${i + 1}`,
          size: i === 0 ? 70 : undefined,
          accessorFn: (row) =>
            row.sensitivityFactors[i]?.value !== undefined
              ? row.sensitivityFactors[i].value
              : "",
          Cell: ({ row }) => {
            // Check if this cell is within the row's actual data or the immediate next cell
            const isWithinRowLength = i < row.original.sensitivityFactors.length;
            
            // Hide cells that are beyond the row's actual factor count.
            // New cells should appear only after Add Variable is clicked.
            if (!isWithinRowLength) {
              return null;
            }

            // Validation: disable if previous value is empty (except for first)
            const prevFilled =
              i === 0 ||
              (row.original.sensitivityFactors[i - 1] &&
                row.original.sensitivityFactors[i - 1].value !== "" &&
                row.original.sensitivityFactors[i - 1].value !== undefined &&
                row.original.sensitivityFactors[i - 1].value !== null);
            const cellValue = row.original.sensitivityFactors[i]?.value;
            const isFilled =
              cellValue !== undefined &&
              cellValue !== null &&
              String(cellValue).trim() !== "";

            // Check cartesian product constraint: max 25 total combinations
            // If adding this cell as a new pair, would the product exceed 25?
            let disableInput = !prevFilled;
            if (i > 0 && !isFilled && prevFilled) {
              // This would be a new pair in this row
              // Calculate current row's filled count (only count pairs with BOTH value AND scaling)
              let currentRowFilledCount = 0;
              for (let k = 0; k < row.original.sensitivityFactors.length; k++) {
                const sf = row.original.sensitivityFactors[k];
                const hasValue = sf.value !== undefined && sf.value !== null && String(sf.value).trim() !== "";
                const hasScaling = sf.scaling !== undefined && sf.scaling !== null && String(sf.scaling).trim() !== "";
                if (hasValue && hasScaling) {
                  currentRowFilledCount++;
                }
              }
              
              // Calculate cartesian product if we add this pair
              let otherRowsProduct = 1;
              for (const otherRow of data) {
                if (otherRow.id === row.original.id) continue; // Skip current row
                let filledCount = 0;
                for (let j = 0; j < otherRow.sensitivityFactors.length; j++) {
                  const sf = otherRow.sensitivityFactors[j];
                  const hasValue = sf.value !== undefined && sf.value !== null && String(sf.value).trim() !== "";
                  const hasScaling = sf.scaling !== undefined && sf.scaling !== null && String(sf.scaling).trim() !== "";
                  if (hasValue && hasScaling) {
                    filledCount++;
                  }
                }
                if (filledCount > 0) otherRowsProduct *= filledCount;
              }
              
              // Project new count: current filled + 1 new pair
              const projectedCurrentRowCount = currentRowFilledCount + 1;
              const projectedProduct = otherRowsProduct * projectedCurrentRowCount;
              disableInput = projectedProduct > MAX_SENSITIVITY_FACTORS;
            }

            if (i === 0) {
              // Show plain text for disabled cell
              return (
                <Box
                  sx={{
                    minHeight: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: isLightMode ? "#000" : "#fff",
                    fontSize: 14,
                    paddingLeft: 8,
                  }}
                >
                  {row.original.sensitivityFactors[0]?.value !== undefined
                    ? String(row.original.sensitivityFactors[0].value)
                    : ""}
                </Box>
              );
            }
            if (disableInput && !isFilled) {
              return null;
            }

            return (
              <TextInput
                value={
                  cellValue !== undefined && cellValue !== null
                    ? String(cellValue)
                    : ""
                }
                error={
                  inputErrors[row.original.id]?.[`value_${i}`] || undefined
                }
                onChange={(e) => {
                  markDirty();
                  const rawValue = e.target.value;
                  const regexMessage = t("simulation.error.invalidFormat");
                  const isValid = validateRegex(
                    rawValue,
                    row.original.attributeRegex
                  );
                  setInputErrors((prev) => {
                    const updated = { ...prev };
                    if (!isValid) {
                      updated[row.original.id] = {
                        ...(updated[row.original.id] || {}),
                        [`value_${i}`]: regexMessage,
                      };
                    } else if (updated[row.original.id]?.[`value_${i}`]) {
                      const { [`value_${i}`]: _, ...restCells } =
                        updated[row.original.id];
                      if (Object.keys(restCells).length === 0) {
                        const { [row.original.id]: __, ...restRows } = updated;
                        return restRows;
                      }
                      updated[row.original.id] = restCells;
                    }
                    return updated;
                  });
                  const newValue = rawValue;
                  setData((old) =>
                    old.map((r) =>
                      r.id === row.original.id
                        ? {
                            ...r,
                            sensitivityFactors: r.sensitivityFactors.map(
                              (sf, idx) => {
                                if (idx === i) {
                                  // If value is cleared, also clear scaling
                                  if (newValue === "") {
                                    return {
                                      ...sf,
                                      value: "",
                                      scaling: "",
                                    };
                                  }
                                  const firstValue = Number(
                                    r.sensitivityFactors[0]?.value
                                  );
                                  let newScaling = sf.scaling;
                                  const parsedVal = Number(newValue);
                                  if (
                                    Number.isFinite(firstValue) &&
                                    Number.isFinite(parsedVal) &&
                                    firstValue !== 0
                                  ) {
                                    newScaling = parsedVal / firstValue;
                                  }
                                  return {
                                    ...sf,
                                    value: newValue,
                                    scaling: newScaling,
                                  };
                                }
                                return sf;
                              }
                            ),
                          }
                        : r
                    )
                  );
                }}
                size="xs"
                styles={{
                  input: {
                    minHeight: 40,
                    color: isLightMode ? "#000" : "#fff",
                    WebkitTextFillColor: isLightMode ? "#000" : "#fff",
                  },
                }}
                classNames={{
                  input: "mantine-disabled-white-placeholder",
                }}
                disabled={disableInput}
                type="text"
                inputMode="decimal"
                pattern="[0-9.]*"
              />
            );
          },
        },
        {
          id: `scaling_${i}`,
          header: `${t("simulation.table.scaling")} ${i + 1}`,
          size: i === 0 ? 70 : undefined,
          accessorFn: (row) =>
            row.sensitivityFactors[i]?.scaling !== undefined
              ? row.sensitivityFactors[i].scaling
              : "",
          Cell: ({ row }) => {
            // Check if this cell is within the row's actual data or the immediate next cell
            const isWithinRowLength = i < row.original.sensitivityFactors.length;
            
            // Hide cells that are beyond the row's actual factor count.
            // New cells should appear only after Add Variable is clicked.
            if (!isWithinRowLength) {
              return null;
            }

            const prevFilled =
              i === 0 ||
              (row.original.sensitivityFactors[i - 1] &&
                row.original.sensitivityFactors[i - 1].value !== "" &&
                row.original.sensitivityFactors[i - 1].value !== undefined &&
                row.original.sensitivityFactors[i - 1].value !== null);
            const cellValue = row.original.sensitivityFactors[i]?.scaling;
            const isFilled =
              cellValue !== undefined &&
              cellValue !== null &&
              String(cellValue).trim() !== "";

            // Check cartesian product constraint: max 25 total combinations
            // If adding this cell as a new pair, would the product exceed 25?
            let disableInput = !prevFilled;
            if (i > 0 && !isFilled && prevFilled) {
              // This would be a new pair in this row
              // Calculate current row's filled count (only count pairs with BOTH value AND scaling)
              let currentRowFilledCount = 0;
              for (let k = 0; k < row.original.sensitivityFactors.length; k++) {
                const sf = row.original.sensitivityFactors[k];
                const hasValue = sf.value !== undefined && sf.value !== null && String(sf.value).trim() !== "";
                const hasScaling = sf.scaling !== undefined && sf.scaling !== null && String(sf.scaling).trim() !== "";
                if (hasValue && hasScaling) {
                  currentRowFilledCount++;
                }
              }
              
              // Calculate cartesian product if we add this pair
              let otherRowsProduct = 1;
              for (const otherRow of data) {
                if (otherRow.id === row.original.id) continue; // Skip current row
                let filledCount = 0;
                for (let j = 0; j < otherRow.sensitivityFactors.length; j++) {
                  const sf = otherRow.sensitivityFactors[j];
                  const hasValue = sf.value !== undefined && sf.value !== null && String(sf.value).trim() !== "";
                  const hasScaling = sf.scaling !== undefined && sf.scaling !== null && String(sf.scaling).trim() !== "";
                  if (hasValue && hasScaling) {
                    filledCount++;
                  }
                }
                if (filledCount > 0) otherRowsProduct *= filledCount;
              }
              
              // Project new count: current filled + 1 new pair
              const projectedCurrentRowCount = currentRowFilledCount + 1;
              const projectedProduct = otherRowsProduct * projectedCurrentRowCount;
              disableInput = projectedProduct > MAX_SENSITIVITY_FACTORS;
            }

            if (i === 0) {
              // Show plain text for disabled cell
              return (
                <Box
                  sx={{
                    minHeight: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: isLightMode ? "#000" : "#fff",
                    fontSize: 14,
                    paddingLeft: 8,
                  }}
                >
                  {row.original.sensitivityFactors[0]?.scaling !== undefined
                    ? String(row.original.sensitivityFactors[0].scaling)
                    : ""}
                </Box>
              );
            }
            if (disableInput && !isFilled) {
              return null;
            }

            return (
              <TextInput
                value={
                  cellValue !== undefined && cellValue !== null
                    ? String(cellValue)
                    : ""
                }
                onChange={(e) => {
                  markDirty();
                  const rawScaling = e.target.value.replace(/[^0-9.]/g, "");
                  // Clamp stored value to 3 digits and 2 decimals so the UI reflects the latest input
                  const parts = rawScaling.split(".");
                  const intPartRaw = (parts[0] || "").slice(0, 3);
                  const decRaw = parts[1] ?? "";
                  const decPart = decRaw.slice(0, 2);
                  let intPart = intPartRaw;
                  if (intPart.length > 1) {
                    intPart = intPart.replace(/^0+/, "") || "0";
                  }
                  let newScaling =
                    decPart !== "" ? `${intPart}.${decPart}` : intPart;
                  // Preserve trailing dot while user is typing decimals (e.g., "12.")
                  if (rawScaling.endsWith(".") && decPart === "") {
                    newScaling = `${intPart}.`;
                  }
                  setData((old) =>
                    old.map((r) =>
                      r.id === row.original.id
                        ? {
                            ...r,
                            sensitivityFactors: r.sensitivityFactors.map(
                              (sf, idx) => {
                                if (idx === i) {
                                  // If scaling is cleared, also clear value
                                  if (newScaling === "") {
                                    return {
                                      ...sf,
                                      scaling: "",
                                      value: "",
                                    };
                                  }
                                  const firstValue =
                                    Number(r.sensitivityFactors[0]?.value) || 0;
                                  let newValue = sf.value;
                                  if (
                                    firstValue &&
                                    !isNaN(Number(newScaling))
                                  ) {
                                    newValue = firstValue * Number(newScaling);
                                  }
                                  const regexMessage = t("simulation.error.invalidFormat");
                                  const isValid = validateRegex(
                                    newValue === undefined || newValue === null
                                      ? ""
                                      : String(newValue),
                                    r.attributeRegex
                                  );
                                  if (!isValid) {
                                    setInputErrors((prev) => ({
                                      ...prev,
                                      [r.id]: {
                                        ...(prev[r.id] || {}),
                                        [`value_${i}`]: regexMessage,
                                      },
                                    }));
                                  } else {
                                    setInputErrors((prev) => {
                                      if (!prev[r.id]?.[`value_${i}`]) {
                                        return prev;
                                      }
                                      const { [`value_${i}`]: _v, ...restCells } =
                                        prev[r.id];
                                      if (Object.keys(restCells).length === 0) {
                                        const { [r.id]: _, ...restRows } = prev;
                                        return restRows;
                                      }
                                      return {
                                        ...prev,
                                        [r.id]: restCells,
                                      };
                                    });
                                  }
                                  return {
                                    ...sf,
                                    scaling: newScaling,
                                    value: newValue,
                                  };
                                }
                                return sf;
                              }
                            ),
                          }
                        : r
                    )
                  );
                }}
                size="xs"
                styles={{
                  input: {
                    minHeight: 40,
                    color: isLightMode ? "#000" : "#fff",
                    WebkitTextFillColor: isLightMode ? "#000" : "#fff",
                  },
                }}
                classNames={{
                  input: "mantine-disabled-white-placeholder",
                }}
                disabled={disableInput}
                type="text"
                inputMode="decimal"
                pattern="[0-9.]*"
              />
            );
          },
        }
      );
    }
    return cols;
  }

  // Columns for the table (without the action column)
  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: t("simulation.table.ida"),
        size: 2,
      },
      {
        accessorKey: "asset",
        header: t("simulation.table.asseta"),
        Cell: ({ row }) => {
          const currentRowId = row.original.id;
          const usedPairs = usedAssetPropertyPairs(currentRowId);
          const assetSelectOptions = assetOptions?.map((opt) => ({
            value: opt.asset,
            label: opt.asset === "Baseload genset" ? "Genset" : opt.asset,
            disabled: opt.properties.every((prop) =>
              usedPairs.includes(`${opt.asset}|||${prop}`)
            ),
          }));
          return (
            <Select
              data={assetSelectOptions}
              value={row.original.asset}
              onChange={(value) => {
                const availableProps = getPropertyOptions(
                  initialSimualtionTable,
                  value
                );
                let newProperty =
                  availableProps.find(
                    (prop) => !usedPairs.includes(`${value}|||${prop}`)
                  ) || "";
                if (!newProperty && availableProps.length > 0) {
                  newProperty = availableProps[0];
                }
                if (usedPairs.includes(`${value}|||${newProperty}`)) {
                  setErrorRows((e) => ({
                      ...e,
                      [currentRowId]: t("simulation.error.duplicateAssetProperty"),
                    }));
                  return;
                } else {
                  setErrorRows((e) => ({ ...e, [currentRowId]: undefined }));
                }
                setInputErrors((prev) => {
                  const { [currentRowId]: _, ...rest } = prev;
                  return rest;
                });
                // Find correct componentId for selected asset and property
                let newComponentId = "";
                let attributeType = "COMPONENT";
                  let attributeRegex = "";
                let blocks = [];
                for (const project of initialSimualtionTable || []) {
                  for (const comp of project.components) {
                    if (comp.componentType === value) {
                      const sv = comp.sensitivityValue.find(
                        (sv) => sv.attributeName === newProperty
                      );
                      if (sv) {
                        newComponentId = comp.componentId;
                        attributeType = sv.attributeType || "COMPONENT";
                          attributeRegex = sv.attributeRegex || "";
                        blocks = sv.sensitivityValues || [];
                        break;
                      }
                    }
                  }
                  if (newComponentId) break;
                }
                setData((old) => {
                  let safeBlocks = Array.isArray(blocks) ? blocks.slice() : [];
                  if (safeBlocks.length > MAX_SENSITIVITY_FACTORS) {
                    safeBlocks = safeBlocks.slice(0, MAX_SENSITIVITY_FACTORS);
                  }
                  return old.map((r) => {
                    if (r.id === currentRowId) {
                      // Only update the changed row
                      let factors = safeBlocks.map((sf) => ({
                        value: sf.attributeValue ?? sf.value ?? "",
                        scaling: sf.scalingFactor ?? sf.scaling ?? "",
                      }));
                      return {
                        ...r,
                        asset: value,
                        property: newProperty,
                        componentId: newComponentId,
                        attributeType,
                        attributeRegex,
                        sensitivityFactors: factors,
                      };
                    }
                    // Don't modify other rows at all
                    return r;
                  });
                });
                markDirty();
              }}
              size="xs"
              styles={{
                input: { minHeight: 40, fontSize: 16 },
                item: { fontSize: 16 },
              }}
              withinPortal
              error={!!errorRows[currentRowId]}
              searchable
            />
          );
        },
      },
      {
        accessorKey: "property",
        header: t("simulation.table.property"),
        Cell: ({ row }) => {
          const currentRowId = row.original.id;
          const asset = row.original.asset;
          const usedPairs = usedAssetPropertyPairs(currentRowId);
          // Build property options with attributeDesc
          let propertyOptions = [];
          for (const project of initialSimualtionTable || []) {
            for (const comp of project.components) {
              if (comp.componentType === asset) {
                for (const sv of comp.sensitivityValue) {
                  propertyOptions.push({
                    value: sv.attributeName,
                    label: sv.attributeDesc
                      ? `${sv.attributeDesc}`
                      : sv.attributeName,
                    desc: sv.attributeDesc || "",
                    disabled: usedPairs.includes(
                      `${asset}|||${sv.attributeName}`
                    ),
                  });
                }
              }
            }
          }
          return (
            <Select
              data={propertyOptions}
              value={row.original.property}
              onChange={(value) => {
                const selectedOption = propertyOptions.find(
                  (opt) => opt.value === value
                );
                const attributeDesc = selectedOption ? selectedOption.desc : "";
                if (usedPairs.includes(`${asset}|||${value}`)) {
                  setErrorRows((e) => ({
                    ...e,
                    [currentRowId]: "Duplicate asset/property not allowed",
                  }));
                  return;
                } else {
                  setErrorRows((e) => ({ ...e, [currentRowId]: undefined }));
                }
                setInputErrors((prev) => {
                  const { [currentRowId]: _, ...rest } = prev;
                  return rest;
                });
                // Get blocks from initialSimualtionTable for the selected asset/property
                let blocks = getSensitivityBlocks(
                  initialSimualtionTable,
                  asset,
                  value
                );
                let attributeRegex = "";
                for (const project of initialSimualtionTable || []) {
                  for (const comp of project.components) {
                    if (comp.componentType === asset) {
                      const sv = comp.sensitivityValue.find(
                        (sv) => sv.attributeName === value
                      );
                      if (sv) {
                        attributeRegex = sv.attributeRegex || "";
                        break;
                      }
                    }
                  }
                }
                if (!Array.isArray(blocks)) blocks = [];
                let safeBlocks = Array.isArray(blocks) ? blocks.slice() : [];
                if (safeBlocks.length > MAX_SENSITIVITY_FACTORS) {
                  safeBlocks = safeBlocks.slice(0, MAX_SENSITIVITY_FACTORS);
                }
                setData((old) => {
                  return old.map((r) => {
                    if (r.id === currentRowId) {
                      // Only update the changed row
                      let factors = safeBlocks.map((sf) => ({
                        value: sf.attributeValue ?? sf.value ?? "",
                        scaling: sf.scalingFactor ?? sf.scaling ?? "",
                      }));
                      return {
                        ...r,
                        property: value,
                        attributeDesc,
                        attributeRegex,
                        sensitivityFactors: factors,
                      };
                    }
                    // Don't modify other rows at all
                    return r;
                  });
                });
                markDirty();
              }}
              size="xs"
              withinPortal
              styles={{
                input: { minHeight: 40, fontSize: 16 },
                item: { fontSize: 16 },
              }}
              error={!!errorRows[currentRowId]}
              searchable
            />
          );
        },
      },
      // Remove the "Sensitivity Factors" header and just spread the columns
      ...getSensitivityColumns(),
    ],
    [data, maxBlocks, assetOptions, initialSimualtionTable, isLightMode, inputErrors]
  );

  // Row actions menu (always last column, handled by enableRowActions)
  const renderRowActions = ({ row }) => (
    <Menu withinPortal position="bottom-end" shadow="md">
      <Menu.Target>
        <ActionIcon aria-label={`simulation-row-actions-${row.original.id}`} variant="subtle" color="gray">
          <IconDotsVertical size={18} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          onClick={() => {
            // Add new empty pair ONLY to the selected row (cap at MAX_SENSITIVITY_FACTORS)
            setData((old) => {
              const result = old.map((r) => {
                if (r.id === row.original.id && canAddVariableForRow(r, old)) {
                  return {
                    ...r,
                    sensitivityFactors: [
                      ...r.sensitivityFactors,
                      { value: "", scaling: "" },
                    ],
                  };
                }
                return r;
              });
              return result;
            });
            markDirty();
            setTimeout(() => {
              if (tableContainerRef.current) {
                tableContainerRef.current.scrollLeft =
                  tableContainerRef.current.scrollWidth;
              }
            }, 100);
          }}
          disabled={!canAddVariableForRow(row.original)}
        >
          Add Value
        </Menu.Item>
        <Menu.Item
          onClick={() => {
            // Remove the last FILLED pair (non-empty), not just the last pair in array
            setData((old) => {
              const result = old.map((r) => {
                if (r.id === row.original.id) {
                  // Find the last filled pair (work backwards from the end)
                  let lastFilledIndex = -1;
                  for (let i = r.sensitivityFactors.length - 1; i > 0; i--) {
                    const sf = r.sensitivityFactors[i];
                    const isFilled = (sf.value !== "" && sf.value !== undefined && sf.value !== null) ||
                                    (sf.scaling !== "" && sf.scaling !== undefined && sf.scaling !== null);
                    if (isFilled) {
                      lastFilledIndex = i;
                      break;
                    }
                  }
                  
                  // If we found a filled pair, remove it and keep only up to that point + one empty
                  if (lastFilledIndex > 0) {
                    let updated = r.sensitivityFactors.slice(0, lastFilledIndex);
                    return {
                      ...r,
                      sensitivityFactors: updated,
                    };
                  } else if (r.sensitivityFactors.length > 1) {
                    // Fallback: if no filled pair found, just remove the last pair
                    let updated = r.sensitivityFactors.slice(0, -1);
                    return {
                      ...r,
                      sensitivityFactors: updated,
                    };
                  }
                }
                return r;
              });
              return result;
            });
            markDirty();
          }}
          disabled={
            row.original.sensitivityFactors.length <= 1
          }
          color="red"
        >
          Delete Value
        </Menu.Item>
        <Menu.Item
          color="red"
          onClick={() => {
            setData((old) =>
              old
                .filter((r) => r.id !== row.original.id)
                .map((r, idx) => ({
                  ...r,
                  id: idx + 1,
                }))
            );
            setInputErrors((prev) => {
              const { [row.original.id]: _, ...rest } = prev;
              return rest;
            });
            markDirty();
          }}
        >
          Delete Component
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  // Shared restriction logic for Add Parameter and Add Sensitivity Factors
  function isAddRowDisabled() {
    if (data.length === 0) return false;
    
    // Restrict if already max 20 rows
    if (data.length >= MAX_ROWS) return true;
    
    // Check cartesian product - if already at max 25, can't add more rows
    // (because even 1 pair in a new row would need product to stay ≤ 25)
    const currentProduct = getCartesianProduct(data);
    if (currentProduct >= MAX_SENSITIVITY_FACTORS) return true;
    
    // If all possible asset/property pairs are already used, disable
    const allPairs = [];
    for (const project of initialSimualtionTable || []) {
      for (const comp of project.components) {
        for (const sv of comp.sensitivityValue) {
          allPairs.push({
            key: `${comp.componentType}|||${sv.attributeName}`,
            sensVals:
              sv.sensitivityValues && sv.sensitivityValues.length > 0
                ? sv.sensitivityValues
                : [{ attributeValue: "", scalingFactor: "" }],
          });
        }
      }
    }
    const usedPairs = new Set(data.map((r) => `${r.asset}|||${r.property}`));
    const availablePairs = allPairs.filter((pair) => !usedPairs.has(pair.key));
    if (availablePairs.length === 0) return true;

    if (data[0]?.sensitivityFactors.length === 0) return true;
    return false;
  }

  // Only show table if there is at least one row
  return (
    <Box   style={{
                pointerEvents: "auto",
                opacity: selectedVersion?.versionStatus === "COMPLETED" ? 0.8 : 1,
              }}>
      <MantineReactTable
        columns={columns}
        data={data}
        enablePagination={false}
        initialState={{
          density: "xs",
          columnPinning: {
            left: ["id", "asset", "property"],
            right: ["mrt-row-actions"],
          },
        }}
        enableTopToolbar={false}
        enableStickyHeader={true}
        enableColumnFilterModes={false}
        enableRowActions={true}
        positionActionsColumn="last"
        renderRowActions={renderRowActions}
        enableGlobalFilter={false}
        enableColumnFilters={false}
        enableSorting={false}
        enableColumnActions={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enableDensityToggle={false}
        enableColumnResizing={false}
        renderBottomToolbarCustomActions={() => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              marginRight: "8px",
              marginTop: 10,
            }}
          >
              <Button
              aria-label="simulation-add-parameter"
              style={{
                marginLeft: 8,
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 500,
              }}
              onClick={() => {
                // Collect all possible asset/property pairs from initialSimualtionTable
                const allPairs = [];
                for (const project of initialSimualtionTable || []) {
                  for (const comp of project.components) {
                    for (const sv of comp.sensitivityValue) {
                      // Use the correct property name: "sensitivityValues"
                      const sensVals =
                        sv.sensitivityValues && sv.sensitivityValues.length > 0
                          ? sv.sensitivityValues
                          : [{ attributeValue: "", scalingFactor: "" }];
                      allPairs.push({
                        asset: comp.componentType,
                        property: sv.attributeName,
                        componentId: comp.componentId,
                        attributeType: sv.attributeType,
                        attributeRegex: sv.attributeRegex,
                        sensVals,
                      });
                    }
                  }
                }
                // Find the first pair not already in data
                const usedPairs = new Set(
                  data.map((r) => `${r.asset}|||${r.property}`)
                );
                // For new row, always use the number of sensitivity values for that property (not maxLen of table)
                const toAdd = allPairs.find(
                  (p) => !usedPairs.has(`${p.asset}|||${p.property}`)
                );
                if (toAdd) {
                  // Keep this row's original blocks and only append one editable empty pair.
                  let factors = [];
                  const sensVals =
                    toAdd.sensVals && toAdd.sensVals.length > 0
                      ? toAdd.sensVals
                      : [{ attributeValue: "", scalingFactor: "" }];
                  for (let i = 0; i < sensVals.length; i++) {
                    factors.push({
                      value: sensVals[i]?.attributeValue ?? "",
                      scaling: sensVals[i]?.scalingFactor ?? "",
                    });
                  }
                  // Cap to MAX_SENSITIVITY_FACTORS - do not auto-add empty pairs
                  if (factors.length > MAX_SENSITIVITY_FACTORS) {
                    factors = factors.slice(0, MAX_SENSITIVITY_FACTORS);
                  }

                  // Do not pad all rows to the same length; that creates visible but locked extra cells.
                  setData((old) => {
                    return [
                      ...old,
                      {
                        id: old.length + 1,
                        asset: toAdd.asset,
                        property: toAdd.property,
                        componentId: toAdd.componentId,
                        attributeType: toAdd.attributeType,
                        attributeRegex: toAdd.attributeRegex,
                        sensitivityFactors: factors,
                      },
                    ];
                  });
                  markDirty();
                  setTimeout(() => {
                    if (tableContainerRef.current) {
                      tableContainerRef.current.scrollTop =
                        tableContainerRef.current.scrollHeight;
                    }
                  }, 150);
                }
              }}
              disabled={isActionButtonsBlocked || isAddRowDisabled() || !userScope?.access?.saveProjectSensitivity}
              sx={{ pointerEvents: isActionButtonsBlocked ? "none" : "auto" }}
              >
              {t("simulation.button.addParameter")}
            </Button>
            &nbsp;&nbsp;
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-start",
                gap: 16,
                width: "100%",
              }}
            >
             

              <Button
                aria-label="simulation-submit"
                variant="filled"
                color="green"
                onClick={handleSubmitClick}
                disabled={isActionButtonsBlocked || data.length === 0 || !userScope?.access?.saveProjectSensitivity}
                sx={{
                  alignSelf: "center",
                  pointerEvents: isActionButtonsBlocked ? "none" : "auto",
                }}
              >
                {t("simulation.button.submit")}
              </Button>
               {/* <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-end",
                  gap: 8,
                }}
              >
                <Box
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: isLightMode ? "#495057" : "#d5d7e0",
                    whiteSpace: "pre-wrap",
                    // lineHeight: 1.4,
                  }}
                >
                  {addVariableGuidance}
                </Box>
                <Box
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: isLightMode ? "#495057" : "#d5d7e0",
                    // padding: "6px 12px",
                    // backgroundColor: isLightMode ? "#f0f0f0" : "#2a2a2a",
                    borderRadius: "4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {`Possibilities: ${cartesianSummary.expression} = ${cartesianSummary.total} | Left: ${cartesianSummary.remaining}`}
                </Box>
              </Box> */}
            </Box>
            {/* <Box sx={{ ml: 2, fontWeight: 500, fontSize: 14 }}>
              {`Value/Scale pairs: ${filledCells} / 25`}
            </Box> */}
            &nbsp;
          </Box>
        )}
        mantineTableContainerProps={{
          sx: {
            backgroundColor: isLightMode
              ? "#FFFFFF"
              : "var(--se-background-alternative)",
            overflowY: "auto",
            maxHeight: "80vh",
            position: "relative",
          },
          ref: tableContainerRef,
        }}
        mantinePaperProps={{
          withBorder: false,
          shadow: false,
          sx: {
            backgroundColor: isLightMode
              ? "#FFFFFF"
              : "var(--se-background-alternative)",
            // Remove overflowY here to avoid scroll conflict
          },
        }}
        mantineTableHeadProps={{
          sx: {
            backgroundColor: isLightMode
              ? "#FFFFFF"
              : "var(--se-background-alternative)",
          },
        }}
        mantineTableHeadCellProps={{
          sx: {
            fontWeight: "normal",
            fontSize: "14px",
            fontFamily: "Nunito",
            backgroundColor: isLightMode
              ? "#FFFFFF"
              : "var(--se-background-alternative)",
            // Remove position: sticky from here
          },
        }}
        mantineTableBodyProps={{
          sx: {
            backgroundColor: isLightMode
              ? "#FFFFFF"
              : "var(--se-background-alternative)",
          },
        }}
        mantineTableBodyCellProps={{
          sx: {
            fontWeight: "normal",
            fontSize: "14px",
            fontFamily: "Nunito",
            backgroundColor: isLightMode
              ? "#FFFFFF"
              : "var(--se-background-alternative)",
          },
        }}
        mantineTableProps={{
          sx: {
            backgroundColor: isLightMode
              ? "#FFFFFF"
              : "var(--se-background-alternative)",
          },
        }}
        mantineTopToolbarProps={{
          sx: {
            backgroundColor: isLightMode
              ? "#FFFFFF"
              : "var(--se-background-alternative)",
          },
        }}
        mantineBottomToolbarProps={{
          sx: {
            backgroundColor: isLightMode
              ? "#FFFFFF"
              : "var(--se-background-alternative)",
          },
        }}
        state={{ isLoading: loading }}
      />
      {paramLimitError && (
        <Box sx={{ color: "red", fontWeight: 600, marginTop: 8 }}>
          {t("simulation.error.only25Params")}
        </Box>
      )}
      <SEConfirmModal
        opened={showSubmitConfirm}
        onClose={() => setShowSubmitConfirm(false)}
        onConfirm={handleConfirmSubmit}
        header={t("common.alert")}
        confirmLabel={t("common.yes")}
        cancelLabel={t("common.no")}
        message={t("simulation.confirm.scenarioDataExists")}
      />
    </Box>
  );
}

SimulationTable.propTypes = {
  initialSimualtionTable: PropTypes.array,
  initialSensitivityData: PropTypes.array,
  callSnackbar: PropTypes.func,
  apiSimulationData: PropTypes.array,
  setIsSubmitClicked: PropTypes.func,
  setSensitivityDirty: PropTypes.func,
  onSensitivityChange: PropTypes.func,
  onCartesianChange: PropTypes.func,
};
